"use client";
import { useState, useRef, useCallback, useEffect } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { Button } from "@ui/button";

const YOUTUBE_VIDEO_ID = "ltdJrHMvMI4";

export const HeroVideoSection = () => {
  const [isMuted, setIsMuted] = useState(false);
  const playerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isReady, setIsReady] = useState(false);

  const createPlayer = useCallback(() => {
    if (playerRef.current) return;

    playerRef.current = new (window as any).YT.Player("hero-yt-player", {
      videoId: YOUTUBE_VIDEO_ID,
      playerVars: {
        autoplay: 1,
        mute: 0,
        loop: 1,
        playlist: YOUTUBE_VIDEO_ID,
        controls: 0,
        showinfo: 0,
        modestbranding: 1,
        rel: 0,
        playsinline: 1,
        disablekb: 1,
        iv_load_policy: 3,
        fs: 0,
        cc_load_policy: 0,
        origin: window.location.origin,
      },
      events: {
        onReady: (event: any) => {
          event.target.playVideo();
          setIsReady(true);
        },
        onStateChange: (event: any) => {
          // Loop: if ended, replay
          if (event.data === (window as any).YT.PlayerState.ENDED) {
            event.target.playVideo();
          }
        },
      },
    });
  }, []);

  // Load the YouTube IFrame API once
  useEffect(() => {
    if ((window as any).YT && (window as any).YT.Player) {
      createPlayer();
      return;
    }

    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.head.appendChild(tag);

    (window as any).onYouTubeIframeAPIReady = () => {
      createPlayer();
    };

    return () => {
      (window as any).onYouTubeIframeAPIReady = null;
    };
  }, [createPlayer]);

  const toggleMute = () => {
    if (!playerRef.current) return;
    if (isMuted) {
      playerRef.current.unMute();
      playerRef.current.setVolume(100);
    } else {
      playerRef.current.mute();
    }
    setIsMuted(!isMuted);
  };

  return (
    <section
      ref={containerRef}
      className="relative w-full h-[60vh] md:h-[80vh] overflow-hidden bg-healing-950"
    >
      {/* YouTube Player (covers entire section) */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{
            width: "177.78vh", // 16:9 ratio
            height: "56.25vw",
            minWidth: "100%",
            minHeight: "100%",
          }}
        >
          <div id="hero-yt-player" className="w-full h-full" />
        </div>
      </div>

      {/* Subtle overlay */}
      <div className="absolute inset-0 bg-black/20 pointer-events-none" />

      {/* Gradient edges */}
      <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-background to-transparent pointer-events-none" />
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background to-transparent pointer-events-none" />

      {/* Mute / Unmute button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleMute}
        className={`absolute bottom-8 right-8 z-20 rounded-full w-12 h-12 backdrop-blur-md border transition-all duration-300 ${
          isReady ? "opacity-100" : "opacity-0"
        } bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white`}
        aria-label={isMuted ? "Unmute video" : "Mute video"}
      >
        {isMuted ? (
          <VolumeX className="w-5 h-5" />
        ) : (
          <Volume2 className="w-5 h-5" />
        )}
      </Button>
    </section>
  );
};
